/**
 * One visit's checklist + photos PER SUPPLIER, for Linda / the masters on Rep Sales & Routing → Visits (2026-10-16,
 * Helena: "where do I go to see what the photos look like"). Read only. Same data the rep's store page saves:
 * POST /field/supplier-checks {id} (ticks + photo ids per supplier, shown names) and POST /field/visit-photo
 * {image_id} (the photo, bearer token — never a public URL). Tap a photo for full size.
 */
import { useEffect, useState } from "react"
import axios from "axios"
import {
  Box, Flex, Text, Badge, Image, Spinner, SimpleGrid, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
} from "@chakra-ui/react"
import { FaCheck, FaMinus } from "react-icons/fa"
import { neutral } from "../../theme/tokens"

const API = process.env.REACT_APP_API_URL

const Photo = ({ imageId, body, size = "120px", onOpen }) => {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    let alive = true
    let made = null
    axios.post(`${API}/field/visit-photo`, { ...body, image_id: imageId }, { responseType: "blob" })
      .then((r) => { if (!alive) return; made = URL.createObjectURL(r.data); setUrl(made) })
      .catch(() => {})
    return () => { alive = false; if (made) URL.revokeObjectURL(made) }
  }, [imageId]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Box w={size} h={size} borderRadius="8px" overflow="hidden" bg={neutral[100]} border="1px solid" borderColor={neutral[200]}
      cursor={url ? "zoom-in" : "default"} onClick={() => url && onOpen && onOpen(url)}>
      {url ? <Image src={url} w="100%" h="100%" objectFit="cover" alt="" /> : <Flex h="100%" align="center" justify="center"><Spinner size="xs" /></Flex>}
    </Box>
  )
}

const shown = (code, names) => {
  const n = names && names[code]
  const norm = (s) => String(s || "").replace(/[^a-z0-9]/gi, "").toUpperCase()
  return n && norm(n) !== norm(code) ? n : code
}

const VisitChecklistModal = ({ visit, body, onClose }) => {
  const [data, setData] = useState(null)
  const [big, setBig] = useState(null)
  useEffect(() => {
    if (!visit) return
    setData(null)
    axios.post(`${API}/field/supplier-checks`, { ...body, id: visit.id })
      .then((r) => setData({ checks: r.data.data || {}, questions: r.data.questions || [], names: r.data.names || {} }))
      .catch(() => setData({ checks: {}, questions: [], names: {}, error: true }))
  }, [visit]) // eslint-disable-line react-hooks/exhaustive-deps

  const sups = data ? Object.keys(data.checks).sort((a, b) => shown(a, data.names).localeCompare(shown(b, data.names))) : []
  return (
    <>
      <Modal isOpen={!!visit} onClose={onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="16px" pr={10}>
            {visit ? `${visit.site_description || ""}${visit.site_code ? ` (${visit.site_code})` : ""}` : ""}
            <Text fontSize="13px" fontWeight={400} color={neutral[500]}>{visit ? `${visit.rep_name} · ${visit.plan_date}` : ""}</Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={5}>
            {!data ? <Spinner size="sm" /> : data.error ? <Text fontSize="13px" color="red.500">Could not load this visit's checklist.</Text>
              : !sups.length ? <Text fontSize="13px" color={neutral[500]}>No ticks or photos for this visit.</Text>
              : sups.map((s) => {
                const o = data.checks[s]
                return (
                  <Box key={s} mb={4} p={3} border="1px solid" borderColor={neutral[200]} borderRadius="10px">
                    <Flex align="center" gap={2} mb={2}>
                      <Text fontWeight={700} fontSize="14px">{shown(s, data.names)}</Text>
                      <Badge colorScheme={o.photos ? "green" : "gray"}>{o.photos} photo{o.photos === 1 ? "" : "s"}</Badge>
                    </Flex>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={1} mb={o.images && o.images.length ? 3 : 0}>
                      {data.questions.map((q) => (
                        <Flex key={q.key} align="center" gap={2} fontSize="13px" color={o[q.key] ? neutral[800] : neutral[400]}>
                          <Box as={o[q.key] ? FaCheck : FaMinus} color={o[q.key] ? "green.500" : neutral[300]} flexShrink={0} />
                          <Text>{q.label}</Text>
                        </Flex>
                      ))}
                    </SimpleGrid>
                    {o.images && o.images.length > 0 && (
                      <Flex wrap="wrap" gap={2}>
                        {o.images.map((im) => <Photo key={im.id} imageId={im.id} body={body} onOpen={setBig} />)}
                      </Flex>
                    )}
                  </Box>
                )
              })}
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal isOpen={!!big} onClose={() => setBig(null)} size="full">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" onClick={() => setBig(null)}>
          <ModalCloseButton color="white" />
          <Flex h="100vh" align="center" justify="center" p={4}>{big && <Image src={big} maxH="92vh" maxW="96vw" objectFit="contain" alt="" />}</Flex>
        </ModalContent>
      </Modal>
    </>
  )
}

export default VisitChecklistModal
